<template>
  <v-app class="app-tabela">
    <div class="box mt-0 mb-5">
      <v-card class="tabela">
        <v-card-title>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar por nome da equipe, CNES ou INE"
            class="input-bottom"
            single-line
            hide-details
          />
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="indexedItems"
          :loading="loading"
          loading-text="Carregando, Por favor espere"
          :search="search"
          :item-class="getRowColor"
          :footer-props="{
            itemsPerPageText: '',
            'items-per-page-options': [10],
            'disable-items-per-page': true,
          }"
          :page="currentPage"
          item-key="index"
          show-expand
          :single-expand="true"
          :expanded.sync="expanded"
          @pagination="getPagination"
          @item-expanded="onItemExpanded"
          class="elevation-1"
        >
          <!-- Rodapé -->
          <template v-slot:[`footer.page-text`]="items">
            <div class="info-table-item tabela-esus-pec"></div>
            <span class="paginacao">
              {{ items.pageStart }} - {{ items.pageStop }} de
              {{ items.itemsLength }} equipes
            </span>
          </template>

          <!-- Coluna de progresso (Vínculo/Acompanhamento) -->
          <template v-slot:item.vinculo_acompanhamento="{ item }">
            <ProgressBar :items="item.dados_vinculo_acompanhamento" />
          </template>

          <!-- Linha expandida: 2 gráficos -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="p-0">
              <div class="row">
                <div class="col-lg-6">
                  <CidadaosVinculadosChart
                    :series="expandedItem.vinculados.series"
                    nivel="2"
                  />
                </div>
                <div class="col-lg-6">
                  <CidadaosAcompanhadosChart
                    :series="expandedItem.acompanhados.series"
                    nivel="2"
                  />
                </div>
              </div>
            </td>
          </template>

          <template v-slot:progress>
            <v-progress-linear color="blue" :height="10" indeterminate />
          </template>

          <template v-slot:no-data>
            <div class="alert alert-info mb-0">
              <b>Nenhuma informação encontrada. Selecione outro filtro.</b>
            </div>
          </template>

          <template v-slot:no-results>
            <div class="alert alert-info mb-0">
              <b>Nenhuma informação encontrada. Selecione outro filtro.</b>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-app>
</template>

<script>
import ProgressBar from './ProgressBar.vue'
import CidadaosVinculadosChart from './CidadaosVinculadosChart.vue'
import CidadaosAcompanhadosChart from './CidadaosAcompanhadosChart.vue'

export default {
  name: 'CvatDataTable',
  components: {
    ProgressBar,
    CidadaosVinculadosChart,
    CidadaosAcompanhadosChart,
  },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
      headers: [
        { text: 'CNES', align: 'start', filterable: true, value: 'nu_cnes' },
        { text: 'UBS', align: 'start', filterable: true, value: 'no_ubs' },
        {
          text: 'INE',
          align: 'start',
          filterable: true,
          value: 'ine',
          width: '150px',
        },
        {
          text: 'Equipe',
          align: 'start',
          filterable: true,
          value: 'no_equipe',
        },
        {
          text: 'Vínculo e Acompanhamento',
          align: 'start',
          sortable: false,
          filterable: false,
          value: 'vinculo_acompanhamento',
          width: '420px',
        },
        { text: '', value: 'data-table-expand' },
      ],
      currentPage: 1,
      expanded: [],
      expandedItem: {
        vinculados: { series: [] },
        acompanhados: { series: [] },
      },
    }
  },
  computed: {
    indexedItems() {
      return this.items.map((el, index) => {
        const vinculo = Number(el.escore_param_cad ?? 0)
        const acomp = Number(el.escore_param_acomp ?? 0)
        const dadosVA = [
          { value: vinculo, label: 'Vínculo' },
          { value: acomp, label: 'Acompanhamento' },
        ]
        return {
          index,
          ...el,
          dados_vinculo_acompanhamento: dadosVA,
        }
      })
    },
  },
  methods: {
    /**
     * Atualiza a página atual da tabela.
     * @param {Object} p Dados da paginação
     */
    getPagination(p) {
      this.currentPage = p.page
    },

    /**
     * Manipula o evento de expansão de um item da tabela.
     * @param {Object} ctx Contexto do item expandido
     */
    onItemExpanded(ctx) {
      const it = ctx?.item || {}

      // vinculados
      const cadParam = Number(it.cad_param ?? 0)
      const cadPec = Number(it.cad_pec ?? 0)
      const cad075 = Number(it.qtd_cad0_75 ?? 0)
      const cad15 = Number(it.qtd_cad1_5 ?? 0)

      this.expandedItem.vinculados.series = [
        { data: [cadParam * 1.5, cadParam, cadPec, cad075, cad15] },
      ]

      // acompanhados
      const cadAcomp = Number(it.qtd_cad_acomp ?? 0)
      const acomp10 = Number(it.qtd_acomp1_0 ?? 0)
      const acomp12 = Number(it.qtd_acomp1_2 ?? 0)
      const acomp13 = Number(it.qtd_acomp1_3 ?? 0)
      const acomp25 = Number(it.qtd_acomp2_5 ?? 0)

      this.expandedItem.acompanhados.series = [
        {
          data: [
            Math.abs(cadPec - cadAcomp),
            acomp10,
            acomp12,
            acomp13,
            acomp25,
          ],
        },
      ]
    },

    /**
     * Retorna a classe CSS da linha da tabela.
     *
     * @param {Object} item
     */
    getRowColor(item) {},
  },
}
</script>

<style scoped></style>
